import { useQueryClient } from '@tanstack/react-query';
import { useOrganizationDirectoryContactsQuery } from '../gpanel-contacts-queries';
import GpanelContactsGrid from './gpanel-contacts/gpanel-contacts-grid/gpanel-contacts-grid';
import { GPanelContactsKeys } from '../gpanel-contacts-api';
import { GPanelContactsPagesConfig } from '../shared/pages-config';
import GPanelContactsDeleteDialog from './gpanel-contacts/gpanel-contacts-delete-dialog';
import { useState } from 'react';
import { useGpanelContactsPermissions } from '../shared/hooks/use-gpanel-contacts-permissions';
import { GPanelContactsPermissions } from '../../../../utils/roles/client-roles';
import TooltipWithPermission from '../shared/components/tooltip-with-permission';
import GPanelContactsManageLabelDialog from './gpanel-contacts/gpanel-contacts-manage-label-dialog.tsx';
import GPanelContactsModal from './gpanel-contacts/gpanel-contacts-form-page';
import GPanelContactsImportDialog from './gpanel-contacts/gpanel-contacts-import-dialog.tsx';
import { useUserSettings } from '../../../../utils/use-user-settings.tsx';
import SharedIconButton from '../../../../shared/buttons/shared-icon-button.tsx';

export default function GpanelOrganizationDirectoryGrid() {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const { PERMISSIONS } = useGpanelContactsPermissions();
  const query = useOrganizationDirectoryContactsQuery();
  const queryClient = useQueryClient();
  const { viewType } = useUserSettings();

  const [addToLabelAnchorEl, setAddToLabelAnchorEl] =
    useState<null | HTMLElement>(null);
  const [openCreateContact, setOpenCreateContact] = useState(false);
  const [openImportContact, setOpenImportContact] = useState(false);

  return (
    <GpanelContactsGrid
      query={query}
      gridKey={viewType.gpanelContactsGridStates?.activeGridState}
      title={GPanelContactsPagesConfig.OrganizationDirectory.name}
      onRefresh={() => {
        query.refetch();
        queryClient.invalidateQueries({
          queryKey: [GPanelContactsKeys.LabelGroups],
        });
      }}
      withHeaderActions
      contactsToolbarActions={(selection) => (
        <>
          {/* Add to Label */}
          <TooltipWithPermission
            hasPermission={PERMISSIONS.modifyGpanelContactLabelMembers}
            permissionName={
              GPanelContactsPermissions.ModifyGpanelContactLabelMembers.name
            }
            renderElement={(hasPermission) => (
              <SharedIconButton
                variant="label"
                disabled={!hasPermission || !selection?.length}
                onClick={(e) => {
                  setAddToLabelAnchorEl(e.currentTarget);
                }}
                disableTooltip
              />
            )}
          />
          {Boolean(addToLabelAnchorEl) && selection?.length && (
            <GPanelContactsManageLabelDialog
              handleClose={() => {
                setAddToLabelAnchorEl(null);
              }}
              anchorEl={addToLabelAnchorEl}
              contactIds={selection as number[]}
              contacts={query.data}
            />
          )}
          {/* Trash Contacts */}
          <TooltipWithPermission
            title="Trash Contacts"
            hasPermission={PERMISSIONS.trashContact}
            permissionName={GPanelContactsPermissions.TrashContact.name}
            renderElement={(hasPermission) => (
              <SharedIconButton
                variant="delete"
                disabled={!hasPermission || !selection?.length}
                onClick={() => setDeleteOpen(true)}
                disableTooltip
              />
            )}
          />
          {deleteOpen && selection?.length && (
            <GPanelContactsDeleteDialog
              contactIds={selection as number[]}
              handleClose={() => setDeleteOpen(false)}
            />
          )}
          {/* Create/Import Contact Dialogs */}
          {openCreateContact && (
            <GPanelContactsModal
              open={true}
              handleClose={() => setOpenCreateContact(false)}
            />
          )}
          <GPanelContactsImportDialog
            open={openImportContact}
            handleClose={() => setOpenImportContact(false)}
          />
        </>
      )}
    />
  );
}
